import * as React from 'react';
import cn from 'classnames';

import { getProgressLevel } from '@features/price-graph';
import { useTheme } from '../../../../../../theme';
import { Money } from '../../../../../../types';
import { Money as MoneyComponent } from '../../../../../../Money';

interface LegendsProps {
	maxPrice: Money;
	minPrice: Money;
	children: (ref: React.Ref<HTMLDivElement>) => React.ReactNode;
}

const Legends: React.FC<LegendsProps> = ({ minPrice, maxPrice, children }) => {
	const { LegendsStyles: css } = useTheme('Features').PriceGraph;

	const contentElRef = React.useRef<HTMLDivElement | null>(null);
	const legendsWrapperRef = React.useRef<HTMLDivElement | null>(null);
	const roRef = React.useRef<ResizeObserver | null>(null);

	const setContentEl = React.useCallback((node: HTMLDivElement | null) => {
		contentElRef.current = node;
	}, []);

	React.useLayoutEffect(() => {
		const contentEl = contentElRef.current;
		const legendsEl = legendsWrapperRef.current;

		if (!contentEl || !legendsEl) {
			return undefined;
		}

		const applyHeight = () => {
			legendsEl.style.height = `${contentEl.getBoundingClientRect().height}px`;
		};

		if (typeof ResizeObserver !== 'undefined') {
			roRef.current = new ResizeObserver(entries => {
				const entry = entries[0];
				if (entry?.contentRect?.height !== null) {
					legendsEl.style.height = `${entry.contentRect.height}px`;
				}
			});
			roRef.current.observe(contentEl);
		} else {
			applyHeight();
		}

		return () => {
			roRef.current?.disconnect();
		};
	}, []);

	const renderLine = (price: Money, level: number, className?: string, specialText?: string) => (
		<div className={cn(css.line, className)} style={{ bottom: `${level}%` }}>
			<div className={css.label}>
				{specialText && <span className={css.label__text}>{specialText}</span>}
				<MoneyComponent moneyClassName={css.money} money={{ amount: price.amount, currency: price.currency }} />
			</div>
		</div>
	);

	const averagePrice = { amount: Math.round(maxPrice.amount / 2), currency: maxPrice.currency };
	const averageLineIsVisible = (averagePrice.amount - minPrice.amount) / averagePrice.amount > 0.4;
	const maxLineIsVisible = (maxPrice.amount - minPrice.amount) / maxPrice.amount > 0.2;
	const isMinAndMaxPricesEqual = minPrice.amount === maxPrice.amount;

	return (
		<>
			<div className={css.legends} ref={legendsWrapperRef}>
				{(maxLineIsVisible || isMinAndMaxPricesEqual) && renderLine(maxPrice, 100, css.line_max)}
				{averageLineIsVisible && renderLine(averagePrice, 50)}
				{!isMinAndMaxPricesEqual && renderLine(minPrice, getProgressLevel(minPrice.amount, maxPrice.amount))}
			</div>
			{children(setContentEl)}
		</>
	);
};

export default Legends;
